import { RefObject, useCallback, useEffect, useState } from 'react';

export const useScrollControl = <T extends HTMLElement = HTMLElement>({
  ref,
  columns,
  callbackDeps,
  deps,
}: {
  ref: RefObject<T>;
  columns: number;
  callbackDeps?: any[];
  deps?: any[];
}) => {
  const [showLeftScrollButton, setShowLeftScrollButton] = useState(false);
  const [showRightScrollButton, setShowRightScrollButton] = useState(true);

  const handleControlledScroll = useCallback(
    (direction: 'left' | 'right' | 'reset') => {
      const scrollView = ref.current;
      if (!scrollView) return;

      const scrollLeft = scrollView.scrollWidth / columns;

      if (direction === 'left') {
        const scrollTo = scrollView.scrollLeft - scrollLeft;
        const hasMoreToScroll = scrollTo > 0;
        scrollView.scrollTo({
          left: scrollTo,
          behavior: 'smooth',
        });
        setShowRightScrollButton(true);
        setShowLeftScrollButton(hasMoreToScroll);
      } else if (direction === 'right') {
        const scrollTo = scrollView.scrollLeft + scrollLeft;
        const hasMoreToScroll = scrollView.scrollWidth - scrollTo > scrollView.clientWidth;
        scrollView.scrollTo({
          left: scrollTo,
          behavior: 'smooth',
        });
        setShowLeftScrollButton(true);
        setShowRightScrollButton(hasMoreToScroll);
      } else {
        const hasMoreToScroll = scrollView.scrollWidth > scrollView.clientWidth;
        scrollView.scrollTo({ left: 0 });
        setShowLeftScrollButton(false);
        setShowRightScrollButton(hasMoreToScroll);
      }
    },
    [ref, columns, ...(callbackDeps ?? [])]
  );

  useEffect(() => {
    const scrollView = ref.current;
    if (!scrollView) return;

    let timeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const scrollWidth = scrollView.scrollWidth;
        const clientWidth = scrollView.clientWidth;
        const scrollLeft = scrollView.scrollLeft;

        setShowLeftScrollButton(scrollLeft > 0);
        setShowRightScrollButton(scrollLeft < scrollWidth - clientWidth);
      }, 100);
    };

    scrollView.addEventListener('scroll', handleScroll);

    handleControlledScroll('reset');

    return () => {
      scrollView.removeEventListener('scroll', handleScroll);
    };
  }, [ref, handleControlledScroll, ...(deps ?? [])]);

  return {
    showLeftScrollButton,
    showRightScrollButton,
    handleControlledScroll,
  };
};
